import { useRef, useState } from "react";
import { EditorRefApi } from "shared/components/Editor";
import { Button, ButtonSize, ButtonVariant, Show } from "shared/components";
import { LiteTextEditor } from "shared/app/Editor/LiteTextEditor";

import { isTextEmpty } from "shared/app/ActivityLog/helpers";
import { NoteData } from "shared/app/TaskTableView/types";
import { cx } from "shared/utils";
import { extractEntitiesFromJson } from "shared/app/Notes/helpers";

interface ActivityNoteProps {
  onSubmit: (data: NoteData) => void;
  onCancel?: () => void;
  initialValue?: string;
  isLoading?: boolean;
}

export const ActivityNote = ({
  onSubmit,
  onCancel,
  initialValue,
  isLoading = false
}: ActivityNoteProps) => {
  const editorRef = useRef<EditorRefApi>(null);
  const [isEmpty, setIsEmpty] = useState(() => isTextEmpty(initialValue));

  return (
    <div className="border-b border-alpha-100 px-6 py-4 space-y-2">
      <div className="border rounded-lg shadow-xs max-h-[100px] overflow-y-auto">
        <LiteTextEditor
          ref={editorRef}
          id="activity-note"
          editable
          initialValue={initialValue}
          placeholder="You can write a note here"
          containerClassName={cx(
            "py-2.5 px-3.5 text-base font-medium",
            "[&_.ProseMirror]:break-words",
            "[&_.ProseMirror]:overflow-wrap-anywhere"
          )}
          onChange={() => {
            const doc = editorRef.current?.getDocument();
            if (doc) setIsEmpty(isTextEmpty(doc.text));
          }}
        />
      </div>

      <Show if={!isEmpty}>
        <div className="flex justify-end gap-2">
          <Button
            variant={ButtonVariant.BASE}
            size={ButtonSize.SM}
            onClick={() => {
              onCancel?.();
              editorRef.current?.clearEditor();
              setIsEmpty(true);
            }}
          >
            Cancel
          </Button>
          <Button
            variant={ButtonVariant.PRIMARY}
            size={ButtonSize.SM}
            disabled={isLoading}
            loading={isLoading}
            onClick={async () => {
              const doc = editorRef.current?.getDocument();
              if (!doc) return;
              const entities = extractEntitiesFromJson(doc.json);

              const value: NoteData = {
                note_content: doc.text,
                document_html: doc.html,
                entities
              };
              onSubmit(value);
              editorRef.current?.clearEditor();
              setIsEmpty(true);
            }}
          >
            Add Note
          </Button>
        </div>
      </Show>
    </div>
  );
};
